import { useEffect, useState, type JSX, type KeyboardEvent, type ReactNode } from 'react';
import { Button, Input } from '@viniciomendesr/rayrovisk-design-system';

export interface ChipOption {
  value: string;
  label: string;
}

/** For the lists whose stored value is the label she reads. */
export function options(...labels: string[]): ChipOption[] {
  return labels.map((label) => ({ value: label, label }));
}

/**
 * A set of chips she can mark, plus a field for the ones nobody could have
 * guessed. `add` is omitted for closed sets, where an extra option would reach
 * code that does not know it.
 */
export function ChipMulti({
  legend,
  hint,
  choices,
  values,
  onChange,
  add
}: {
  legend: string;
  hint?: ReactNode;
  choices: ChipOption[];
  values: string[];
  onChange: (values: string[]) => void;
  add?: string;
}): JSX.Element {
  const [pool, setPool] = useState<ChipOption[]>(choices);

  // A word she saved last time, or typed a moment ago, has to survive being
  // unmarked. Without this it leaves `values`, leaves the screen, and she has
  // to type it again to change her mind.
  useEffect(() => {
    setPool((current) => {
      const missing = values.filter((value) => !current.some((item) => item.value === value));
      return missing.length === 0 ? current : [...current, ...options(...missing)];
    });
  }, [values]);

  return (
    <ChipSet
      legend={legend}
      hint={hint}
      pool={pool}
      isSelected={(option) => values.includes(option.value)}
      onToggle={(option, selected) =>
        onChange(
          selected
            ? [...values, option.value]
            : values.filter((item) => item !== option.value)
        )
      }
      add={add}
      onAdd={(typed) => {
        setPool((current) =>
          current.some((item) => item.value === typed) ? current : [...current, ...options(typed)]
        );
        if (!values.includes(typed)) {
          onChange([...values, typed]);
        }
      }}
    />
  );
}

/** The same set, one answer. `clearable` is off where the field has a default
 * the flow depends on, so a second click cannot leave it empty. */
export function ChipChoice({
  legend,
  hint,
  choices,
  value,
  onChange,
  add,
  clearable = true
}: {
  legend: string;
  hint?: ReactNode;
  choices: ChipOption[];
  value: string;
  onChange: (value: string) => void;
  add?: string;
  clearable?: boolean;
}): JSX.Element {
  const [pool, setPool] = useState<ChipOption[]>(choices);

  useEffect(() => {
    setPool((current) =>
      value === '' || current.some((item) => item.value === value)
        ? current
        : [...current, ...options(value)]
    );
  }, [value]);

  return (
    <ChipSet
      legend={legend}
      hint={hint}
      pool={pool}
      isSelected={(option) => option.value === value}
      onToggle={(option, selected) => {
        if (selected) {
          onChange(option.value);
        } else if (clearable) {
          onChange('');
        }
      }}
      add={add}
      onAdd={(typed) => {
        setPool((current) =>
          current.some((item) => item.value === typed) ? current : [...current, ...options(typed)]
        );
        onChange(typed);
      }}
    />
  );
}

function ChipSet({
  legend,
  hint,
  pool,
  isSelected,
  onToggle,
  add,
  onAdd
}: {
  legend: string;
  hint?: ReactNode;
  pool: ChipOption[];
  isSelected: (option: ChipOption) => boolean;
  onToggle: (option: ChipOption, selected: boolean) => void;
  add?: string;
  onAdd: (typed: string) => void;
}): JSX.Element {
  const [typed, setTyped] = useState('');

  function submit() {
    const word = typed.trim();
    if (word === '') {
      return;
    }
    setTyped('');
    onAdd(word);
  }

  function submitOnEnter(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      event.preventDefault();
      submit();
    }
  }

  /**
   * Leaving the field counts as adding. Without this the word she typed and
   * did not submit dies with the step, and on the required "Seu nicho" that
   * reads as a bug: `Continuar` stays inert saying "Escreva o seu nicho para
   * continuar" while her nicho is on screen, in the box.
   */
  function submitOnBlur() {
    submit();
  }

  return (
    // 8px between the three parts, the same interval `Field` uses, so a chip
    // group and a labelled input read as the same kind of thing in one column.
    <fieldset
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        margin: 0,
        padding: 0,
        border: 0,
        minWidth: 0
      }}
    >
      <legend style={{ padding: 0, font: '500 13.5px/1.4 var(--rvk-font-functional)' }}>
        {legend}
      </legend>
      {hint !== undefined && (
        <p
          style={{
            margin: 0,
            font: '400 13px/1.5 var(--rvk-font-functional)',
            color: 'var(--rvk-text-secondary)'
          }}
        >
          {hint}
        </p>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {pool.map((option) => (
          <Chip
            key={option.value}
            selected={isSelected(option)}
            onToggle={(selected) => onToggle(option, selected)}
          >
            {option.label}
          </Chip>
        ))}
      </div>
      {add !== undefined && (
        // Capped rather than filling the column: the chips are the answer, and
        // a field as wide as the group reads as the primary one.
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', maxWidth: 340 }}>
          <div style={{ flex: '1 1 120px', minWidth: 0 }}>
            <Input
              aria-label={`${legend}: ${add}`}
              placeholder={add}
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              onKeyDown={submitOnEnter}
              onBlur={submitOnBlur}
            />
          </div>
          <Button variant="ghost" onClick={submit}>
            Adicionar
          </Button>
        </div>
      )}
    </fieldset>
  );
}

/**
 * The same hand-rolled chip `LibraryScreen` uses, and for the same reason: of
 * the two tokens that could carry the selected boundary, only
 * `--rvk-state-selected-ring` is promised ≥3:1 against its own surface in every
 * brand × theme. The label stays `--rvk-text-primary`, because
 * `--rvk-accent-active` on `--rvk-accent-subtle` measures 2.31:1 in the orange
 * brand and 13px text owes 4.5:1.
 */
function Chip({
  children,
  selected,
  onToggle
}: {
  children: string;
  selected: boolean;
  onToggle: (selected: boolean) => void;
}): JSX.Element {
  return (
    <button
      type="button"
      className="rvk-focus-ring"
      aria-pressed={selected}
      onClick={() => onToggle(!selected)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        minHeight: 36,
        padding: '7px 14px',
        cursor: 'pointer',
        textAlign: 'left',
        background: selected ? 'var(--rvk-accent-subtle)' : 'var(--rvk-surface-1)',
        color: 'var(--rvk-text-primary)',
        border: `1px solid ${selected ? 'var(--rvk-state-selected-ring)' : 'var(--rvk-border-subtle)'}`,
        borderRadius: 'var(--rvk-radius-full)',
        font: `${selected ? 500 : 400} 13px/1.3 var(--rvk-font-functional)`
      }}
    >
      {children}
    </button>
  );
}
